import React, { useContext, useState, useRef, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { ThemeContext } from '../contexts/ThemeContext';
import { Box, Typography, Button, Container, Card, CardContent, IconButton, Paper, styled, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FaGraduationCap, FaClock, FaStar, FaChalkboardTeacher, FaBookOpen } from "react-icons/fa";
import { MdExpandMore, MdExpandLess } from "react-icons/md";
import CloseIcon from '@mui/icons-material/Close';
import Avatar from '@mui/material/Avatar';
import ContactSection from '../components/ContactSection';
import LightModeLogo from '../assets/images/LT_FK.jpg';
import DarkModeLogo from '../assets/images/LT_FH.jpg';
import img1 from '../assets/images/carousel1.jpg';
import img2 from '../assets/images/carousel2.jpg';
import img3 from '../assets/images/carousel3.jpg';
import img4 from '../assets/images/carousel4.jpg';
import img5 from '../assets/images/carousel5.jpg';
import maganora_bg from '../assets/images/maganora_bg.jpg';
import SocialMediaSidebar from '../components/SocialMediaSidebar';
import Footer from '../components/Footer';
import { BRAND, brandPalette } from '../theme/brand';

const images = [img1, img2, img3, img4, img5];

const testimonials = [
  { quote: 'Fantasztikus megközelítés! A gyermekem imádja az órákat és alig várja a következőt. Nagyon sokat fejlődött rövid idő alatt.', author: 'Andrea', title: 'Lelkes Szülő', rating: 5 },
  { quote: 'A magánórák rugalmasak és nagyon hatékonyak. A tananyagot a gyermek egyéni tempójához igazították.', author: 'Éva', title: 'Elégedett Ügyfél', rating: 5 },
  { quote: 'Problémamegoldó képességei látványosan javultak. Már nem fél a kihívásoktól, és kreatívabban áll hozzá a feladatokhoz.', author: 'Benedek', title: 'Büszke Szülő', avatar: "https://images.unsplash.com/photo-1582294158931-e403d6d06d44?auto=format&fit=crop&q=80&w=1935&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D", rating: 5 },
];

const features = [
  { icon: <FaChalkboardTeacher />, title: "Szakértő oktatók", description: "Tanulj hiteles szakemberektől, akik a legújabb pedagógiai módszereket alkalmazzák." },
  { icon: <FaClock />, title: "Rugalmas időbeosztás", description: "Válaszd ki a számodra legmegfelelőbb időpontokat és tempót." },
  { icon: <FaGraduationCap />, title: "Személyre szabott tanulás", description: "Az órák a te egyéni igényeidhez és tanulási stílusodhoz igazodnak." },
  { icon: <FaBookOpen />, title: "Átfogó tananyag", description: "Strukturált és logikus tanösvény a sikeres fejlődésért." }
];

// Styled Components
const StyledHeroSection = styled(Box)(({ theme }) => ({
  backgroundImage: `url(${maganora_bg})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  padding: theme.spacing(10, 2),
  [theme.breakpoints.up("md")]: { padding: theme.spacing(15, 2) },
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "linear-gradient(135deg, rgba(201,43,181,0.55), rgba(109,27,119,0.65) 50%, rgba(18,10,57,0.85))"
  }
}));

const TestimonialCard = styled(Card)(({ theme }) => ({
  margin: theme.spacing(1),
  backgroundImage: "none",
  backgroundColor: theme.palette.mode === "dark" ? "rgba(29, 18, 71, 0.88)" : "#FFFFFF",
  border: theme.palette.mode === "dark" ? "1px solid rgba(252, 198, 50, 0.28)" : "1px solid rgba(109, 27, 119, 0.18)",
  borderRadius: 16,
  height: "100%",
  transition: "transform 0.3s",
  "&:hover": {
    transform: "translateY(-5px)"
  }
}));

// Kerekített, animált gomb (sötét módban sárga, világos módban lila)
const AnimatedRoundedButton = styled(Button)(({ theme }) => {
  const dark = theme.palette.mode === 'dark';
  const p = brandPalette(dark);
  return {
    borderRadius: '50px',
    backgroundColor: p.btnBg,
    color: p.btnText,
    fontWeight: 700,
    textTransform: 'none',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      backgroundColor: p.btnHoverBg,
      color: dark ? BRAND.purpleDark : '#FFFFFF',
      transform: 'scale(1.05)',
      boxShadow: '0px 6px 15px rgba(0, 0, 0, 0.3)',
    },
    '&:active': {
      transform: 'scale(0.95)',
      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.2)',
    },
  };
});


const MaganoraPage = () => {
  const { darkMode } = useContext(ThemeContext)!;
  const pal = brandPalette(darkMode);
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [contentHeight, setContentHeight] = useState(200);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, []);

  const currentLogo = darkMode ? LightModeLogo : DarkModeLogo;

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleNavigateToConsultation = () => {
    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth', block: 'end' }); 
    }
    setOpenDialog(false);
  };

  return (
    <Box sx={{ pt: '64px', backgroundColor: pal.pageBg, }}>
      <Navbar />
      <SocialMediaSidebar />

      {/* Főoldali Hero Szekció */}
      <StyledHeroSection>
        <Container maxWidth="lg">
          <Box position="relative" zIndex={1} textAlign="center">
            <Typography variant="h2" color="white" gutterBottom sx={{ fontSize: { xs: "2rem", sm: "2.8rem", md: "3.75rem" }, fontWeight: 800, textShadow: "0 2px 8px rgba(0,0,0,0.5)" }}>
              Alakítsd át a tanulási utad
            </Typography>
            <Typography variant="h5" color="white" paragraph sx={{ fontSize: { xs: "1.05rem", md: "1.5rem" } }}>
              Tapasztald meg a személyre szabott oktatást a céljaid és időbeosztásod szerint.
            </Typography>
            <AnimatedRoundedButton variant="contained" size="large" onClick={handleOpenDialog}>
              Foglalj le egy órát
            </AnimatedRoundedButton>
          </Box>
        </Container>
      </StyledHeroSection>

      {/* Funkciók Szekció */}
      <Container maxWidth="lg">
        <Box py={8} sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
          {features.map((feature, index) => (
            <Box key={index} sx={{ flex: '1 1 200px' }}>
              <Paper elevation={0} sx={{ p: 3, height: '100%', textAlign: 'center', backgroundColor: pal.card, border: pal.cardBorder, boxShadow: pal.cardShadow, borderRadius: 3 }}>
                <IconButton disableRipple sx={{ fontSize: 40, color: pal.accent }}>
                  {feature.icon}
                </IconButton>
                <Typography variant="h6" gutterBottom sx={{ color: pal.heading, fontWeight: 700 }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" sx={{ color: pal.muted }}>
                  {feature.description}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>
      </Container>
      
      <Box py={6} sx={{ backgroundColor: pal.pageBg, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            backgroundColor: pal.card,
            border: pal.cardBorder,
            color: pal.text,
            padding: { xs: 2.5, md: 5 },
            borderRadius: 3,
            boxShadow: pal.cardShadow,
            width: { xs: '90%', md: '70%' },
            textAlign: 'center',
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ color: pal.heading, fontWeight: 800, fontSize: { xs: "1.5rem", md: "2.125rem" } }}>
            Magánórák – Együtt a siker felé
          </Typography>
          <Box
            sx={{
              maxHeight: expanded ? `${contentHeight}px` : '200px',
              overflow: 'hidden',
              position: 'relative',
              transition: 'max-height 0.7s ease-in-out',
              maskImage: expanded ? 'none' : 'linear-gradient(to bottom, black 50%, transparent 100%)',
              textAlign: 'justify',
            }}
          >
            <div ref={contentRef}>
              <Typography paragraph>
                Gyakran hallják a gyerekek, hogy „tanulj jobban!”, de azt már ritkán mutatja meg nekik valaki, hogyan is kell csinálni. Itt jövök a képbe én, Csillag, nem mint szigorú tanár, hanem mint egy segítő útitárs, aki több éves tapasztalattal támogatja a gyerekeket abban, hogy rátaláljanak a saját útjukra a tanulásban.
              </Typography>
              <Typography paragraph>
                Az óráimon nem csupán a felzárkóztatás a cél. Az elsődleges feladatunk az, hogy felfedezzük és megerősítsük azokat az egyedi képességeket, amelyek minden gyermekben ott rejtőznek. A foglalkozások személyre szabottak, játékosak, de nagyon is komoly céllal: hogy a gyermekek megtanuljanak bízni magukban és a saját erejükben.
              </Typography>
              <Typography paragraph>
                Nincs sablon, nincsenek kötelező módszerek. Ehelyett van **egyéni odafigyelés** és apró, megfontolt lépések, amelyek végül hatalmas változásokhoz vezetnek. Büszkén mondhatom, hogy több gyermek fejlődését is végigkísérhettem, akik az elején még bizonytalanok voltak, de végül magabiztos diákokká váltak. Néhányukkal olyannyira sikerült megszerettetni a matematikát, hogy ma már versenyekre készülünk együtt.
              </Typography>
              <Typography paragraph>
                Óvodás kortól egészen a felső tagozatig kísérem a diákjaimat. A legkisebbekkel iskolaelőkészítő vagy képességfejlesztő sakk foglalkozáson találkozunk, alsó tagozatosként szinte bármely tantárgyban tudok segíteni, felsősöknek pedig elsősorban **matematikából**, akár a felvételire való felkészítésig is. A diákjaim különösen szeretik, hogy a bonyolultabb témákat is aprólékosan, lépésről lépésre magyarázom el, ahogyan ezt a kisiskolás évek alatt megszokták.
              </Typography>
              <Typography paragraph>
                A távolság sem akadály: az online térben hasonlóan jó hangulatú órákat tartok, mint a Tanodámban. A személyes kötődés a virtuális térben is erős marad, és így a távolabb élő gyerekek is részesei lehetnek ennek a fejlődésnek.
              </Typography>
              <Typography paragraph>
                Miért érdemes a Tanodámat választani? Azért, mert nálam a gyermek nem csupán felzárkózik, hanem **tartósan motiváltabbá és magabiztosabbá válik**. Gyermekagykontrollos és tanulásmódszertani tudásommal segítem őket, hogy ne csak a tananyagot értsék meg, de megtanulják, hogyan is kell tanulni. A szülőkkel folyamatosan tartjuk a kapcsolatot, így garantált, hogy a gyermek a számára legmegfelelőbb úton halad, nem kényszerből, hanem belső indíttatásból.
              </Typography>
              <Typography paragraph>
                A legfontosabb visszajelzés számomra a sok nevetés, az ölelések, és a családok szeretete.  Imádom a Tanodámat, és ha tehetném, kikiabálnám a világba: Csillag vagyok, a Csillagtanoda tanítója!
              </Typography>
              <Typography paragraph>
                Ha szeretnéd, hogy a gyermeked ne csak a lemaradását pótolja, hanem egy belső iránytűt is kapjon, amely végigkíséri az életében, szeretettel várom a Tanodámban. Kisvárda és környékén személyesen, távolabbról pedig online, vagy a letölthető tananyagaim segítségével. Nálam a tanulás nem csak egy kötelező feladat, hanem egy személyes, pozitív élmény.
              </Typography>
            </div>
          </Box>
          <AnimatedRoundedButton
            variant="contained"
            onClick={handleExpandClick}
            startIcon={expanded ? <MdExpandLess /> : <MdExpandMore />}
            sx={{ mt: 2 }}
          >
            {expanded ? 'Összecsukás' : 'Tovább olvasom'}
          </AnimatedRoundedButton>
        </Box>
        {/* Logó */}
        <Box
          component="img"
          src={currentLogo}
          alt="Letölthető tartalmak logó"
          sx={{
            width: 120,
            height: 220,
            cursor: 'pointer',
            mt: 2,
            borderRadius: 0,
            transition: 'transform 0.3s',
            '&:hover': {
              transform: 'scale(1.05)',
            },
          }}
          onClick={() => navigate('/letolthetotartalmak')}
        />
      </Box>

      <Box py={8} sx={{ backgroundColor: pal.band }}>
        <Container maxWidth="lg">
          <Typography variant="h3" gutterBottom align="center" sx={{ color: pal.heading, fontWeight: 800, fontSize: { xs: "2rem", md: "3rem" } }}>
            Vélemények
          </Typography>
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 4,
          }}>
            {testimonials.map((testimonial, index) => (
              <Box key={index} sx={{ flex: '1 1 300px' }}>
                <TestimonialCard>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Avatar
                        src={testimonial.avatar}
                        sx={{ width: 60, height: 60, marginRight: 2 }}
                      />
                      <Box>
                        <Typography variant="h6">{testimonial.author}</Typography>
                        <Typography variant="subtitle2" sx={{ color: pal.muted }}>{testimonial.title}</Typography>
                        <Box display="flex">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <FaStar key={i} color={BRAND.yellow} />
                          ))}
                        </Box>
                      </Box>
                    </Box>
                    <Typography variant="body1" sx={{ fontStyle: 'italic', color: pal.text }}>
                      "{testimonial.quote}"
                    </Typography>
                  </CardContent>
                </TestimonialCard>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Szekció */}
      <Box py={8} px={2} textAlign="center" sx={{ backgroundColor: pal.pageBg }}>
        <Typography variant="h4" gutterBottom sx={{ color: pal.heading, fontWeight: 800 }}>
          Készen állsz az indulásra?
        </Typography>
        <Typography variant="body1" paragraph sx={{ color: pal.text }}>
          Csatlakozz a sikeres tanulók közösségéhez még ma.
        </Typography>
        <AnimatedRoundedButton variant="contained" size="large" onClick={handleNavigateToConsultation}>
          Ingyenes konzultáció kérése
        </AnimatedRoundedButton>
      </Box>
      
      {/* Az új komponens beillesztése a ContactSection-nel */}
      <Box sx={{ py: 8, backgroundColor: pal.pageBg, }}>
          <div id="contact-section">
            <ContactSection handleOpen={handleOpenDialog} />
          </div>
      </Box>
      
      <Footer />

      {/* Dialog ablak */}
        <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        disableScrollLock
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 3,
            textAlign: 'center',
            maxWidth: 450,
            backgroundImage: 'none',
            border: pal.cardBorder,
          },
        }}
      >
        <IconButton
          aria-label="close"
          onClick={handleCloseDialog}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        
        <Box sx={{ p: 2 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
            Nincs szabad időpont 😔
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            Sajnos jelenleg nincs szabad időpont magánórákra.
            Kérlek, foglalj egy ingyenes konzultációt, ahol megbeszéljük, hogyan tudok segíteni neked.
          </Typography>
        </Box>
        
        <AnimatedRoundedButton
          onClick={handleNavigateToConsultation}
          variant="contained"
          size="large"
          sx={{ mt: 3, textTransform: 'none' }}
          fullWidth
        >
          Ingyenes konzultáció kérése
        </AnimatedRoundedButton>
      </Dialog>
    </Box>
  );
};

export default MaganoraPage;